import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import {
  Users, DollarSign, FileText, Crown, ArrowLeft, Search,
  ArrowUpCircle, ArrowDownCircle, Loader2, TrendingUp, BarChart3,
  Activity, Zap, ShoppingBag, Heart, Ticket, Palette, Mail
} from 'lucide-react';
import { ConversionFunnelDashboard } from '../components/admin/ConversionFunnelDashboard';
import { RevenueAnalyticsDashboard } from '../components/admin/RevenueAnalyticsDashboard';
import { RepeatPurchaseAnalytics } from '../components/admin/RepeatPurchaseAnalytics';
import { CouponManagement } from '../components/admin/CouponManagement';
import { CanvaTemplateManagement } from '../components/admin/CanvaTemplateManagement';
import { NewsletterManagement } from '../components/admin/NewsletterManagement';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Admin = () => {
  const navigate = useNavigate();
  const { user, isAdmin, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [updatingUser, setUpdatingUser] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [timeRange, setTimeRange] = useState(30);

  useEffect(() => {
    if (!authLoading && (!user || !isAdmin)) {
      navigate('/dashboard');
    }
  }, [user, isAdmin, authLoading, navigate]);

  const fetchData = useCallback(async () => {
    if (!user || !isAdmin) return;
    try {
      const [statsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/users`)
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users || usersRes.data);
    } catch (err) { console.error('Failed to fetch admin data:', err); } finally {
      setLoading(false);
    }
  }, [user, isAdmin]);

  useEffect(() => {
    if (user && isAdmin) {
      fetchData();
    }
  }, [user, isAdmin, fetchData]);

  const fetchAnalytics = useCallback(async (days) => {
    setAnalyticsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/analytics?days=${days}`);
      setAnalytics(res.data);
    } catch (err) { console.error('Failed to fetch analytics:', err); }
    setAnalyticsLoading(false);
  }, []);

  useEffect(() => {
    if (activeTab === 'analytics' && user && isAdmin && !analytics) {
      fetchAnalytics(timeRange);
    }
  }, [activeTab, user, isAdmin, analytics, timeRange, fetchAnalytics]);

  const handleTimeRangeChange = (days) => {
    setTimeRange(days);
    setAnalytics(null);
    fetchAnalytics(days);
  };

  const handleUpgrade = async (userId) => {
    setUpdatingUser(userId);
    try {
      await axios.post(`${API}/admin/users/${userId}/upgrade`);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, subscription_tier: 'pro' } : u));
      setStats(prev => prev ? {
        ...prev,
        pro_users: prev.pro_users + 1,
        free_users: prev.free_users - 1
      } : prev);
    } catch (error) {
      console.error('Upgrade error:', error);
    }
    setUpdatingUser(null);
  };

  const handleDowngrade = async (userId) => {
    setUpdatingUser(userId);
    try {
      await axios.post(`${API}/admin/users/${userId}/downgrade`);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, subscription_tier: 'free' } : u));
      setStats(prev => prev ? {
        ...prev,
        pro_users: prev.pro_users - 1,
        free_users: prev.free_users + 1
      } : prev);
    } catch (error) {
      console.error('Downgrade error:', error);
    }
    setUpdatingUser(null);
  };

  const filteredUsers = users.filter(u =>
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.full_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#F7F5F2] dark:bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-primary animate-spin mx-auto mb-4" />
          <p className="font-sans text-stone-500 text-sm">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F5F2] dark:bg-background" data-testid="admin-page">
      {/* Nav */}
      <nav className="border-b border-[#d8cfc8]/50 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
                <img src="/lumera-logo.png" alt="Lumera" className="w-8 h-8 rounded-full" />
                <span className="font-serif font-semibold text-lg text-foreground">Lumera</span>
              </div>
              <span className="text-[10px] font-sans font-bold uppercase tracking-widest text-white bg-[#111111] px-2 py-0.5 rounded-full">Admin</span>
            </div>
            <Button
              data-testid="admin-back-btn"
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              className="text-stone-500 font-sans text-xs h-8"
            >
              <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Dashboard
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-stone-900">Admin Panel</h1>
          <p className="font-sans text-stone-500 text-sm mt-1">Platform overview and user management</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="admin-stats-grid">
          <StatCard
            testId="stat-total-users"
            label="Total Users"
            value={stats?.total_users || 0}
            sub={`${stats?.free_users || 0} free, ${stats?.pro_users || 0} pro`}
            icon={Users}
            color="bg-blue-50 text-blue-600"
          />
          <StatCard
            testId="stat-pro-users"
            label="Pro Users"
            value={stats?.pro_users || 0}
            sub={stats?.total_users > 0 ? `${((stats.pro_users / stats.total_users) * 100).toFixed(1)}% conversion` : '0%'}
            icon={Crown}
            color="bg-amber-50 text-amber-600"
          />
          <StatCard
            testId="stat-revenue"
            label="Revenue"
            value={`$${(stats?.total_revenue || 0).toFixed(0)}`}
            sub="All time"
            icon={DollarSign}
            color="bg-green-50 text-green-600"
          />
          <StatCard
            testId="stat-projects"
            label="Projects"
            value={stats?.total_projects || 0}
            sub="Created by users"
            icon={FileText}
            color="bg-purple-50 text-purple-600"
          />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-stone-100 rounded-full p-1 w-fit mb-6 overflow-x-auto max-w-full" data-testid="admin-tabs">
          <button
            data-testid="tab-overview"
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 rounded-full text-xs font-sans font-semibold transition-all ${
              activeTab === 'overview' ? 'bg-white shadow-sm text-stone-900' : 'text-stone-500 hover:text-stone-700'
            }`}
          >
            <BarChart3 className="w-3.5 h-3.5 inline mr-1.5" />Overview
          </button>
          <button
            data-testid="tab-users"
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 rounded-full text-xs font-sans font-semibold transition-all ${
              activeTab === 'users' ? 'bg-white shadow-sm text-stone-900' : 'text-stone-500 hover:text-stone-700'
            }`}
          >
            <Users className="w-3.5 h-3.5 inline mr-1.5" />Users ({users.length})
          </button>
          <button
            data-testid="tab-analytics"
            onClick={() => setActiveTab('analytics')}
            className={`px-4 py-2 rounded-full text-xs font-sans font-semibold transition-all ${
              activeTab === 'analytics' ? 'bg-white shadow-sm text-stone-900' : 'text-stone-500 hover:text-stone-700'
            }`}
          >
            <Activity className="w-3.5 h-3.5 inline mr-1.5" />Analytics
          </button>
          <button
            data-testid="tab-funnel"
            onClick={() => setActiveTab('funnel')}
            className={`px-4 py-2 rounded-full text-xs font-sans font-semibold transition-all ${
              activeTab === 'funnel' ? 'bg-white shadow-sm text-stone-900' : 'text-stone-500 hover:text-stone-700'
            }`}
          >
            <ShoppingBag className="w-3.5 h-3.5 inline mr-1.5" />Conversion
          </button>
          <button
            data-testid="tab-revenue"
            onClick={() => setActiveTab('revenue')}
            className={`px-4 py-2 rounded-full text-xs font-sans font-semibold transition-all ${
              activeTab === 'revenue' ? 'bg-white shadow-sm text-stone-900' : 'text-stone-500 hover:text-stone-700'
            }`}
          >
            <DollarSign className="w-3.5 h-3.5 inline mr-1.5" />Revenue
          </button>
          <button
            data-testid="tab-customers"
            onClick={() => setActiveTab('customers')}
            className={`px-4 py-2 rounded-full text-xs font-sans font-semibold transition-all ${
              activeTab === 'customers' ? 'bg-white shadow-sm text-stone-900' : 'text-stone-500 hover:text-stone-700'
            }`}
          >
            <Heart className="w-3.5 h-3.5 inline mr-1.5" />Customers
          </button>
          <button
            data-testid="tab-coupons"
            onClick={() => setActiveTab('coupons')}
            className={`px-4 py-2 rounded-full text-xs font-sans font-semibold transition-all ${
              activeTab === 'coupons' ? 'bg-white shadow-sm text-stone-900' : 'text-stone-500 hover:text-stone-700'
            }`}
          >
            <Ticket className="w-3.5 h-3.5 inline mr-1.5" />Coupons
          </button>
          <button
            data-testid="tab-canva"
            onClick={() => setActiveTab('canva')}
            className={`px-4 py-2 rounded-full text-xs font-sans font-semibold transition-all ${
              activeTab === 'canva' ? 'bg-white shadow-sm text-stone-900' : 'text-stone-500 hover:text-stone-700'
            }`}
          >
            <Palette className="w-3.5 h-3.5 inline mr-1.5" />Canva
          </button>
          <button
            data-testid="tab-newsletter"
            onClick={() => setActiveTab('newsletter')}
            className={`px-4 py-2 rounded-full text-xs font-sans font-semibold transition-all ${
              activeTab === 'newsletter' ? 'bg-white shadow-sm text-stone-900' : 'text-stone-500 hover:text-stone-700'
            }`}
          >
            <Mail className="w-3.5 h-3.5 inline mr-1.5" />Newsletter
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden" data-testid="recent-transactions">
            <div className="px-6 py-4 border-b border-stone-50">
              <h3 className="font-sans font-bold text-stone-800 text-sm flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-green-500" /> Recent Transactions
              </h3>
            </div>
            {stats?.recent_transactions?.length > 0 ? (
              <div className="divide-y divide-stone-50">
                {stats.recent_transactions.map((tx, i) => (
                  <div key={i} className="flex items-center justify-between px-6 py-3 hover:bg-stone-50/50 transition-colors">
                    <div>
                      <p className="font-sans font-medium text-stone-800 text-sm">{tx.user_email}</p>
                      <p className="font-sans text-stone-400 text-xs">{new Date(tx.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-sans font-bold text-stone-800 text-sm">${tx.amount?.toFixed(2)}</p>
                      <span className={`inline-block text-[10px] font-sans font-semibold px-2 py-0.5 rounded-full ${
                        tx.payment_status === 'paid'
                          ? 'bg-green-50 text-green-600'
                          : 'bg-amber-50 text-amber-600'
                      }`}>
                        {tx.payment_status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-6 py-12 text-center">
                <p className="font-sans text-stone-400 text-sm">No transactions yet</p>
              </div>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-4" data-testid="users-management">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <input
                data-testid="user-search-input"
                type="text"
                placeholder="Search users by email or name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-white border border-stone-200 rounded-xl font-sans text-sm text-stone-700 placeholder:text-stone-400 focus:outline-none focus:border-primary transition-colors"
              />
            </div>

            {/* Users Table */}
            <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
              {/* Header */}
              <div className="hidden sm:grid grid-cols-12 gap-4 px-6 py-3 border-b border-stone-100 bg-stone-50/50">
                <div className="col-span-4 font-sans text-[10px] font-bold uppercase tracking-widest text-stone-400">User</div>
                <div className="col-span-2 font-sans text-[10px] font-bold uppercase tracking-widest text-stone-400">Plan</div>
                <div className="col-span-3 font-sans text-[10px] font-bold uppercase tracking-widest text-stone-400">Joined</div>
                <div className="col-span-3 font-sans text-[10px] font-bold uppercase tracking-widest text-stone-400 text-right">Actions</div>
              </div>

              {/* Rows */}
              {filteredUsers.length > 0 ? (
                <div className="divide-y divide-stone-50">
                  {filteredUsers.map((u) => (
                    <React.Fragment key={u.id}>
                    <div
                      data-testid={`user-row-${u.id}`}
                      className="hidden sm:grid grid-cols-12 gap-4 px-6 py-3 items-center hover:bg-stone-50/50 transition-colors"
                    >
                      <div className="col-span-4 min-w-0">
                        <p className="font-sans font-medium text-stone-800 text-sm truncate">{u.email}</p>
                        {u.full_name && (
                          <p className="font-sans text-stone-400 text-xs truncate">{u.full_name}</p>
                        )}
                      </div>
                      <div className="col-span-2">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-sans font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                          u.subscription_tier === 'pro'
                            ? 'bg-amber-50 text-amber-600'
                            : 'bg-stone-100 text-stone-500'
                        }`}>
                          {u.subscription_tier === 'pro' && <Crown className="w-3 h-3" />}
                          {u.subscription_tier || 'free'}
                        </span>
                        {u.is_admin && (
                          <span className="ml-1 text-[10px] font-sans font-bold text-red-500 uppercase">Admin</span>
                        )}
                      </div>
                      <div className="col-span-3">
                        <p className="font-sans text-stone-500 text-xs">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                      <div className="col-span-3 flex items-center justify-end gap-2">
                        {!u.is_admin && (
                          u.subscription_tier === 'pro' ? (
                            <button
                              data-testid={`downgrade-btn-${u.id}`}
                              onClick={() => handleDowngrade(u.id)}
                              disabled={updatingUser === u.id}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-stone-200 text-stone-500 text-[10px] font-sans font-semibold hover:border-red-200 hover:text-red-500 transition-colors disabled:opacity-50"
                            >
                              {updatingUser === u.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <ArrowDownCircle className="w-3 h-3" />
                              )}
                              Downgrade
                            </button>
                          ) : (
                            <button
                              data-testid={`upgrade-btn-${u.id}`}
                              onClick={() => handleUpgrade(u.id)}
                              disabled={updatingUser === u.id}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-amber-50 text-amber-600 text-[10px] font-sans font-semibold hover:bg-amber-100 transition-colors disabled:opacity-50"
                            >
                              {updatingUser === u.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <ArrowUpCircle className="w-3 h-3" />
                              )}
                              Upgrade
                            </button>
                          )
                        )}
                      </div>
                    </div>
                    {/* Mobile card view */}
                    <div className="sm:hidden flex items-center justify-between p-4 hover:bg-stone-50/50 transition-colors" data-testid={`user-row-mobile-${u.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="font-sans font-medium text-stone-800 text-sm truncate">{u.email}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-[10px] font-sans font-bold uppercase px-2 py-0.5 rounded-full ${
                            u.subscription_tier === 'pro' ? 'bg-amber-50 text-amber-600' : 'bg-stone-100 text-stone-500'
                          }`}>{u.subscription_tier || 'free'}</span>
                          {u.is_admin && <span className="text-[10px] font-bold text-red-500">Admin</span>}
                        </div>
                      </div>
                      {!u.is_admin && (
                        u.subscription_tier === 'pro' ? (
                          <button onClick={() => handleDowngrade(u.id)} disabled={updatingUser === u.id}
                            className="flex items-center gap-1 px-2 py-1.5 rounded-lg border border-stone-200 text-stone-500 text-[10px] font-sans font-semibold">
                            {updatingUser === u.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowDownCircle className="w-3 h-3" />} Downgrade
                          </button>
                        ) : (
                          <button onClick={() => handleUpgrade(u.id)} disabled={updatingUser === u.id}
                            className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-amber-50 text-amber-600 text-[10px] font-sans font-semibold">
                            {updatingUser === u.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowUpCircle className="w-3 h-3" />} Upgrade
                          </button>
                        )
                      )}
                    </div>
                    </React.Fragment>
                  ))}
                </div>
              ) : (
                <div className="px-6 py-12 text-center">
                  <p className="font-sans text-stone-400 text-sm">
                    {searchQuery ? 'No users match your search' : 'No users yet'}
                  </p>
                </div>
              )}
            </div>

            <p className="font-sans text-stone-400 text-xs text-center">
              Showing {filteredUsers.length} of {users.length} users
            </p>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6" data-testid="analytics-dashboard">
            {/* Time Range Selector */}
            <div className="flex items-center justify-between">
              <h3 className="font-sans font-bold text-stone-800 text-sm">Growth & Trends</h3>
              <div className="flex items-center gap-1 bg-stone-100 rounded-full p-1" data-testid="time-range-selector">
                {[7, 30, 90].map(d => (
                  <button
                    key={d}
                    data-testid={`range-${d}d`}
                    onClick={() => handleTimeRangeChange(d)}
                    className={`px-3 py-1.5 rounded-full text-[10px] font-sans font-semibold transition-all ${
                      timeRange === d ? 'bg-white shadow-sm text-stone-900' : 'text-stone-500 hover:text-stone-700'
                    }`}
                  >
                    {d}d
                  </button>
                ))}
              </div>
            </div>

            {analyticsLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-[#B49A88]" />
              </div>
            ) : analytics ? (
              <>
                {/* Key Metrics Row */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="analytics-metrics">
                  <MetricCard
                    testId="metric-growth"
                    label="7-Day Growth"
                    value={`${analytics.key_metrics.growth_rate_7d > 0 ? '+' : ''}${analytics.key_metrics.growth_rate_7d}%`}
                    positive={analytics.key_metrics.growth_rate_7d >= 0}
                    icon={TrendingUp}
                  />
                  <MetricCard
                    testId="metric-conversion"
                    label="Conversion Rate"
                    value={`${analytics.conversion_rate}%`}
                    positive={analytics.conversion_rate > 0}
                    icon={Zap}
                  />
                  <MetricCard
                    testId="metric-avg-gens"
                    label="Avg Gens/User"
                    value={analytics.key_metrics.avg_generations_per_user}
                    positive={true}
                    icon={FileText}
                  />
                  <MetricCard
                    testId="metric-recent-signups"
                    label="Signups (7d)"
                    value={analytics.key_metrics.recent_signups_7d}
                    positive={true}
                    icon={Users}
                  />
                </div>

                {/* Charts Row 1: Signups + Revenue */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <ChartCard title="Daily Signups" testId="chart-signups">
                    <ResponsiveContainer width="100%" height={220}>
                      <AreaChart data={analytics.signup_series}>
                        <defs>
                          <linearGradient id="signupGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2}/>
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f4" />
                        <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#a8a29e' }} tickFormatter={formatDate} />
                        <YAxis tick={{ fontSize: 10, fill: '#a8a29e' }} allowDecimals={false} />
                        <Tooltip content={<CustomTooltip />} />
                        <Area type="monotone" dataKey="signups" stroke="#3b82f6" fill="url(#signupGrad)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </ChartCard>

                  <ChartCard title="Revenue" testId="chart-revenue">
                    <ResponsiveContainer width="100%" height={220}>
                      <AreaChart data={analytics.revenue_series}>
                        <defs>
                          <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#22c55e" stopOpacity={0.2}/>
                            <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f4" />
                        <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#a8a29e' }} tickFormatter={formatDate} />
                        <YAxis tick={{ fontSize: 10, fill: '#a8a29e' }} tickFormatter={(v) => `$${v}`} />
                        <Tooltip content={<CustomTooltip prefix="$" />} />
                        <Area type="monotone" dataKey="cumulative" stroke="#22c55e" fill="url(#revGrad)" strokeWidth={2} name="Cumulative" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </div>

                {/* Charts Row 2: Generations + Top Templates */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <ChartCard title="Daily Generations" testId="chart-generations">
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={analytics.generation_series}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f4" />
                        <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#a8a29e' }} tickFormatter={formatDate} />
                        <YAxis tick={{ fontSize: 10, fill: '#a8a29e' }} allowDecimals={false} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="generations" fill="#B49A88" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>

                  <ChartCard title="Top Templates" testId="chart-templates">
                    {analytics.top_templates?.length > 0 ? (
                      <div className="space-y-2 px-1">
                        {analytics.top_templates.map((t, i) => {
                          const max = analytics.top_templates[0]?.count || 1;
                          return (
                            <div key={i} className="flex items-center gap-3">
                              <span className="font-sans text-xs text-stone-500 w-5 text-right">{i + 1}</span>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-0.5">
                                  <span className="font-sans text-xs font-medium text-stone-700 truncate">{t.name}</span>
                                  <span className="font-sans text-[10px] text-stone-400 flex-shrink-0 ml-2">{t.count}</span>
                                </div>
                                <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                                  <div
                                    className="h-full rounded-full bg-gradient-to-r from-[#B49A88] to-[#f4a261]"
                                    style={{ width: `${(t.count / max) * 100}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="font-sans text-stone-400 text-xs text-center py-8">No template data yet</p>
                    )}
                  </ChartCard>
                </div>

                {/* Conversion Pie */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <ChartCard title="User Breakdown" testId="chart-conversion">
                    <ResponsiveContainer width="100%" height={180}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Free', value: stats?.free_users || 0 },
                            { name: 'Pro', value: stats?.pro_users || 0 }
                          ]}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={70}
                          paddingAngle={4}
                          dataKey="value"
                        >
                          <Cell fill="#d6d3d1" />
                          <Cell fill="#B49A88" />
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="flex items-center justify-center gap-4 -mt-2">
                      <span className="flex items-center gap-1.5 text-[10px] font-sans text-stone-500">
                        <span className="w-2 h-2 rounded-full bg-stone-300" /> Free ({stats?.free_users || 0})
                      </span>
                      <span className="flex items-center gap-1.5 text-[10px] font-sans text-[#B49A88]">
                        <span className="w-2 h-2 rounded-full bg-[#B49A88]" /> Pro ({stats?.pro_users || 0})
                      </span>
                    </div>
                  </ChartCard>
                </div>
              </>
            ) : null}
          </div>
        )}

        {/* Conversion Funnel Tab */}
        {activeTab === 'funnel' && (
          <ConversionFunnelDashboard />
        )}

        {/* Revenue & Subscriptions Tab */}
        {activeTab === 'revenue' && (
          <RevenueAnalyticsDashboard />
        )}

        {/* Customers / Repeat Purchase Tab */}
        {activeTab === 'customers' && (
          <RepeatPurchaseAnalytics />
        )}

        {/* Coupons Tab */}
        {activeTab === 'coupons' && (
          <CouponManagement />
        )}

        {activeTab === 'canva' && (
          <CanvaTemplateManagement />
        )}

        {activeTab === 'newsletter' && (
          <NewsletterManagement />
        )}
      </div>
    </div>
  );
};

/* ─── Stat Card ─── */
const StatCard = ({ testId, label, value, sub, icon: Icon, color }) => (
  <div data-testid={testId} className="bg-white rounded-2xl border border-stone-100 p-5">
    <div className="flex items-center justify-between mb-3">
      <span className="font-sans text-xs font-semibold text-stone-400">{label}</span>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
    <p className="font-sans font-bold text-2xl text-stone-900">{value}</p>
    <p className="font-sans text-xs text-stone-400 mt-0.5">{sub}</p>
  </div>
);

/* ─── Metric Card ─── */
const MetricCard = ({ testId, label, value, positive, icon: Icon }) => (
  <div data-testid={testId} className="bg-white rounded-2xl border border-stone-100 p-4">
    <div className="flex items-center gap-2 mb-2">
      <div className={`w-6 h-6 rounded-md flex items-center justify-center ${positive ? 'bg-green-50 text-green-500' : 'bg-red-50 text-red-500'}`}>
        <Icon className="w-3.5 h-3.5" />
      </div>
      <span className="font-sans text-[10px] font-bold uppercase tracking-widest text-stone-400">{label}</span>
    </div>
    <p className={`font-sans font-bold text-xl ${positive ? 'text-stone-900' : 'text-red-500'}`}>{value}</p>
  </div>
);

/* ─── Chart Card ─── */
const ChartCard = ({ title, testId, children }) => (
  <div data-testid={testId} className="bg-white rounded-2xl border border-stone-100 p-5">
    <h4 className="font-sans font-bold text-stone-800 text-xs mb-4">{title}</h4>
    {children}
  </div>
);

/* ─── Custom Tooltip ─── */
const CustomTooltip = ({ active, payload, label, prefix = '' }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-stone-200 rounded-lg shadow-lg px-3 py-2">
      <p className="font-sans text-[10px] text-stone-400 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="font-sans text-xs font-semibold text-stone-800">
          {p.name}: {prefix}{p.value}
        </p>
      ))}
    </div>
  );
};

/* ─── Date Formatter ─── */
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  return `${parts[1]}/${parts[2]}`;
};

export default Admin;
